import { FC, SetStateAction, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PopoverInput } from '@ui/components/Input'
import { FilterOptions } from '@ui/v2/TableFilter/TableFilter'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'
import { ReferralMaterialPreviewDto } from '@model/ReferralMaterialDto'

import { Locale } from '@global/locale/Locale'
import { makeResolveFlagIcon } from '@global/locale/localeIcons'

import { PromoAssetFields } from './PromoPreview.modal'

import styles from './PromoPreviewModal.module.scss'

interface Props {
  isRefMaterialLoading: boolean
  referralMaterial: ReferralMaterialPreviewDto
  caLang: Locale
  fields: PromoAssetFields
  setField: (value: SetStateAction<PromoAssetFields>) => void
  fetchReferralMaterial: (
    referralMaterialId: string,
    caLang?: Locale,
    query?: FilterQueryProps
  ) => Promise<ReferralMaterialPreviewDto>
}

export const PromoSizeLanguageField: FC<Props> = ({
  isRefMaterialLoading,
  referralMaterial,
  caLang,
  fields,
  setField,
  fetchReferralMaterial,
}) => {
  const { t } = useTranslation()

  const sizeOptions = useMemo(() => {
    return referralMaterial.promoAssets
      .filter((promo) => promo.language.id === fields.language?.id)
      .map((promo) => promo.size)
  }, [referralMaterial, fields.language?.id])

  const handleLanguageChange = async (language: NameDto<string>) => {
    if (fields.language?.id === language.id) {
      return
    }

    const promoAsset = await fetchReferralMaterial(referralMaterial.id, caLang, {
      [FilterOptions.LanguageId]: language.id,
    })
    const defaultPromo = promoAsset.promoAssets.find(({ isDefault }) => isDefault)
    setField((prevFields) => ({
      ...prevFields,
      language: language,
      size: defaultPromo?.size ?? null,
    }))
  }

  const handleSizeChange = (size: NameDto<number>) => {
    if (fields.size?.id === size.id) {
      return
    }

    setField((prevFields) => ({ ...prevFields, size }))

    fetchReferralMaterial(referralMaterial.id, caLang, {
      ...(fields.size?.id && { [FilterOptions.LanguageId]: fields.language?.id }),
      [FilterOptions.SizeId]: size.id,
    })
  }

  const languageStartIcon = useMemo(
    () => makeResolveFlagIcon(fields.language?.id),
    [fields.language?.id]
  )

  return (
    <div className={styles.sizeLanguageFields}>
      <PopoverInput
        size='lg'
        onChange={(language) => handleLanguageChange(language)}
        label={t('Language')}
        dropdownListVariant='compact'
        options={referralMaterial.languages}
        isFullWidth
        value={fields.language?.id}
        disabled={isRefMaterialLoading}
        startIcon={languageStartIcon}
      />
      <PopoverInput
        onChange={(size) => handleSizeChange(size)}
        label={t('Referral Materials.Size')}
        dropdownListVariant='compact'
        size='lg'
        options={sizeOptions}
        isFullWidth
        value={fields.size?.id}
        disabled={isRefMaterialLoading}
      />
    </div>
  )
}
